import React from "react";
import DiffPopover from "../Diffpopover";
import { FaEdit } from "react-icons/fa";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import styled from "styled-components";
import UriLink from "./UriLink";
import { IgnBadge } from "./Pills";


function EditButton({ beginEdit, fieldId }) {
    if (typeof beginEdit === 'undefined') return null;
    return (
        <EditBtn title="Edit recommendation" onClick={() => beginEdit(fieldId)}>
            <FaEdit />
        </EditBtn>
    );
}

function needIgnoreBadge(diffDisabled, fieldValueRef) {
    return !diffDisabled && (fieldValueRef === undefined || fieldValueRef == null);
}

function IgnoredBadge() {
    return <IgnBadge title="No recommendation was specified for this field">no recc</IgnBadge>;
}

const isUrl = v => typeof v === 'string' && (v.startsWith('http://') || v.startsWith('https://'));

export function resolveUrl(base, url) {
    if (!url || isUrl(url)) return url;
    try {
        const { origin } = new URL(base);
        return origin + url;
    } catch {
        return url;
    }
}

export function Field(props) {
    const cur = props.fieldValueCur;
    let ref = props.fieldValueRef;
    const displayedText = props.displayedText || cur;
    if (!ref) ref = cur;
    return (
        <FieldRow>
            <FieldLabel>
                {props.fieldName}
                {needIgnoreBadge(props.diffDisabled, props.fieldValueRef) && <IgnoredBadge />}
            </FieldLabel>
            <FieldValue>
                <DiffPopover diffDisabled={props.diffDisabled}
                             name={props.fieldName}
                             inputA={ref}
                             inputB={cur} />
                {isUrl(cur)
                    ? <UriLink url={cur} displayText={displayedText} />
                    : <span>{displayedText}</span>
                }
                {props.children}
            </FieldValue>
            <FieldActions>
                <EditButton {...props} />
            </FieldActions>
        </FieldRow>
    );
}

export function ListField(props) {
    const cur = Array.isArray(props.fieldValueCur) ? props.fieldValueCur.slice(0) : [];
    const ref = props.diffDisabled ? cur : (props.fieldValueRef || cur);

    if (ref.length > cur.length) {
        const diff = ref.length - cur.length;
        for (let i = 0; i < diff; i++) cur.push(null);
    }

    return (
        <FieldRow>
            <FieldLabel>
                {props.fieldName}
                {needIgnoreBadge(props.diffDisabled, props.fieldValueRef) && <IgnoredBadge />}
            </FieldLabel>
            <FieldValue>
                <ListWrap>
                    {cur.map((value, index) => {
                        const resolved = props.baseUrl ? resolveUrl(props.baseUrl, value) : value;
                        return (
                            <div key={'ListField' + index}>
                                <DiffPopover name={props.fieldName}
                                             inputA={ref[index]}
                                             inputB={value} />
                                {isUrl(resolved) && !props.noUriLink
                                    ? <UriLink url={resolved} displayText={value} />
                                    : isUrl(resolved)
                                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <span>{value}</span>
                                        <ExternalIconLink href={resolved} target="_blank" rel="noopener noreferrer" title="Open in new tab" />
                                      </span>
                                    : <span>{value}</span>
                                }
                            </div>
                        );
                    })}
                </ListWrap>
            </FieldValue>
            <FieldActions>
                <EditButton {...props} />
            </FieldActions>
        </FieldRow>
    );
}

ListField.defaultProps = { fieldName: "" };


const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 36px;
  align-items: start;
  gap: 12px;
  padding: 12px 24px;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }
  font-family: "Inter", system-ui, sans-serif;
  @media (max-width: 600px) {
    grid-template-columns: 1fr 28px;
    grid-template-areas: "label actions" "value value";
    padding: 10px 14px;
    gap: 4px 8px;
  }
`;

const FieldLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  padding-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  @media (max-width: 600px) { grid-area: label; }
`;

const FieldValue = styled.div`
  font-size: 0.85rem;
  color: #0f172a;
  word-break: break-word;
  min-width: 0;
  @media (max-width: 600px) { grid-area: value; font-size: 0.78rem; }
`;

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 10em;
  overflow-y: auto;
`;

const FieldActions = styled.div`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 600px) { grid-area: actions; }
`;

function ExternalIconLink({ href, target, rel, title }) {
    return (
        <ExternalLink href={href} target={target} rel={rel} title={title}>
            <OpenInNewIcon style={{ fontSize: '0.75rem' }} />
        </ExternalLink>
    );
}

const ExternalLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    color: #cbd5e1;
    text-decoration: none;
    transition: background 120ms, color 120ms;
    &:hover { background: #ede9fe; color: #6366f1; }
`;

const EditBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background 120ms, color 120ms;
  &:hover { background: #ede9fe; color: #6366f1; border-color: #6366f1; }
`;


